<template>
  <NuxtLayout
    v-if="info"
    :name="setLayout"
  >
    <div class="pt-6 pb-12">
      <div
        class="info-content"
        :data-hix="info?.id"
        v-html="info.html"
      />
      <div
        v-if="isNewsletter"
        class="flex flex-wrap -mx-4"
      >
        <div class="w-12/12 xl:w-6/12 lg:w-8/12 md:w-10/12 px-4">
          <NewsletterForm
            horizontal
            :campaign="newsletterCampaign"
          />
        </div>
      </div>
    </div>
  </NuxtLayout>
</template>

<script setup>
definePageMeta({
  layout: false,
})

const localeStore = useLocaleStore()
const rootStore = useRootStore()
const { urls: localeURLs } = useLocale()

const route = useRoute()
const { t } = useI18n()
const router = useRouter()

const { setPageBanner } = useBanner()

const { error, data } = await useAsyncData(async () => {
  try {
    const allQueryParams = route.fullPath.split('?')?.[1]
    const forwardQueryParams = allQueryParams ? `?${allQueryParams}` : ''

    const { data } = await apiFetch(`/${localeStore.locale}/staticpage/${encodeURIComponent(route.params.slug)}${forwardQueryParams}`)

    const result = data
    setPageBanner({
      title: result.topic,
      size: 'small',
      background: {
        image: {
          url: result.picture_cloudinary,
        },
      },
    })

    return {
      info: result,
    }
  } catch (e) {
    throw new Error('Post not found: ' + router.currentRoute.value.fullPath)
  }
})

if (error.value) {
  throw createError({
    message: error.value.message,
    statusCode: 404,
    stack: error.value.stack,
    fatal: true,
  })
}

const { info } = data.value
const ogImage = rootStore.getOgImage
const { $isSol } = useNuxtApp()

useHead({
  title: () => info?.topic,
  script: () => (info?.scripts || []).map(s => ({
    src: s,
    defer: true,
  })),

  meta: () => [
    {
      hid: 'description',
      name: 'description',
      content: () => info?.meta_description || t('calenderHomeDescription'),
    },
    {
      hid: 'og:url',
      name: 'og:url',
      content: () => `${localeURLs.BASE_URL}/info/${route.params.slug}`,
    },
    {
      hid: 'og:title',
      name: 'og:title',
      content: () => `${info?.topic} - ${t('siteTitleBrand')}`,
    },
    {
      hid: 'og:image',
      name: 'og:image',
      content: () => formatCloudinaryImagePath(ogImage, {
        forceJPG: true,
        isSol: $isSol,
      }),
    },
    {
      hid: 'og:description',
      name: 'og:description',
      content: () => t('calenderHomeDescription'),
    },
  ],
})

const setLayout = computed(() => isSol() ? 'charter-page' : 'banner-default')
const isNewsletter = computed(() => [111, 112, 132, 163, 164, 172].includes(info?.id))
const newsletterCampaign = computed(() => {
  if ([163, 164].includes(info?.id)) {
    return 'discount'
  }

  return undefined
})

onMounted(() => {
  const scriptTag = document.getElementById('mnd-script')
  if (scriptTag) {
    // eslint-disable-next-line no-eval
    eval(scriptTag.innerHTML)
  }
})

// const loadScript = (src) => {
//     const ckeditor = document.createElement('script')
//     ckeditor.setAttribute('src', src)
//     document.head.appendChild(ckeditor)
//   }
</script>

<style lang="scss">
@import "@layers/web/assets/scss/modules/_static-page";
</style>
